/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/header.scss';

import React, {MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';
import {AwesomeIcon, DVRDGroupedSelect, getMode} from '@dvrd/dvr-controls';
import {userHasSubscription} from '../../util/utils';
import userCircle from '@iconify/icons-fa-solid/user-circle';
import signOut from '@iconify/icons-fa/sign-out';
import {Icon} from '@iconify/react';
import {AppContext} from '../misc/appContext';
import {LayoutContext} from '../misc/layoutContext';
import classNames from 'classnames';
import ContextAccountController from '../account/select/contextAccountController';
import SystemMessagesController from '../systemMessage/systemMessagesController';
import Image from '../misc/image';
import HeaderCrumbs from './crumbs/headerCrumbs';
import BolmateDialog from '../controls/dialog/bolmateDialog';
import IDate from '@dvrd/idate';
import {getCurrentLanguage, setUserLanguage} from '../../i18n/languageService';
import {useTranslation} from 'react-i18next';
import {AppLanguage, normalizeLanguage} from '../../i18n/config';

interface Props {
    onClickLogo: MouseEventHandler;
    onClickUpgrade: MouseEventHandler;
    onClickLogout: MouseEventHandler;
    onClickDemoRegister: MouseEventHandler;
    menuWidth: number;
    isDemo: boolean;
    upgradeClicked: boolean;
}

export default function Header(props: Props) {
    const {t, i18n} = useTranslation();
    const context = useContext(AppContext);
    const {sidebarRef, menuCollapsed, toggleMenu} = useContext(LayoutContext);
    const {user} = context.userContext;
    const {onClickLogo, onClickLogout, menuWidth, onClickUpgrade, onClickDemoRegister, isDemo, upgradeClicked} = props;
    const [language, setLanguage] = useState<AppLanguage>(getCurrentLanguage());
    const [accountDialogActive, setAccountDialogActive] = useState(false);
    const [headerHeight, setHeaderHeight] = useState(0);
    // Below break-lg-up (1280px) the "not optimized for mobile" banner shows (see custom.scss); collapse
    // the right-side controls into the dialog at that breakpoint, otherwise show them inline.
    const [collapsed, setCollapsed] = useState(() => window.matchMedia('(max-width: 1279px)').matches);
    const headerRef = useRef<HTMLDivElement>(null);
    const resizeObserver = useRef(
        new ResizeObserver(() => {
            determineHeaderHeight();
        })
    );
    const languageItems = [
        {
            value: 'nl',
            label: (
                <span>
                    <span className='fi fi-nl' style={{margin: '0 0.375rem'}} />{' '}
                    {t('settings.language.nl', 'Nederlands')}
                </span>
            )
        },
        {
            value: 'en',
            label: (
                <span>
                    <span className='fi fi-gb' style={{margin: '0 0.375rem'}} /> {t('settings.language.en', 'Engels')}
                </span>
            )
        }
    ];

    function onDoubleClickEnv(evt: React.MouseEvent) {
        evt.currentTarget.parentElement?.removeChild(evt.currentTarget);
    }

    function determineHeaderHeight() {
        const header = headerRef.current;
        if (!header) return;
        const subWarning = header.querySelector('div.sub-warning'),
            height = header.getBoundingClientRect().height + (subWarning?.getBoundingClientRect().height ?? 0);
        setHeaderHeight(height);
        const menu = sidebarRef.current;
        if (menu) {
            menu.style.height = `calc(100vh - ${height}px)`;
            menu.style.top = height + 'px';
        }
    }

    function attachListener() {
        const header = headerRef.current;
        if (!header) return;
        resizeObserver.current.observe(header);
    }

    function removeListener() {
        const header = headerRef.current;
        if (!header) return;
        resizeObserver.current.unobserve(header);
    }

    function renderEnv() {
        if (isDemo) return renderDemoEnv();
        const mode = getMode();
        let env: string | null = null;
        if (mode === 'accept') env = 'Acceptatie omgeving';
        else if (['test', 'sub', 'comet', 'orbit'].includes(mode)) env = 'Test omgeving';
        if (env)
            return (
                <span className='env' onDoubleClick={onDoubleClickEnv}>
                    {env}
                </span>
            );
        return null;
    }

    function renderDemoEnv() {
        return (
            <div className='demo-header'>
                <span className='demo-label'>{t('header.demoAccount', 'Demo account')}</span>
                <div className='button-container'>
                    <div className={classNames('upgrade-btn', 'blue')} onClick={onClickDemoRegister}>
                        <label className='btn-label'>
                            {t('header.startTrial', 'Ja, ik probeer het nu voor 14 dagen gratis')}
                        </label>
                    </div>
                    <label className='disclaimer'>
                        {t('header.trialDisclaimer', 'Stopt automatisch, geen betaalgegevens nodig')}
                    </label>
                </div>
            </div>
        );
    }

    function renderTrialCounter() {
        const {user} = context.userContext;
        if (!user?.subscription || userHasSubscription(user, false)) return null;
        const {trialEndDate} = user.subscription;
        const now = IDate.now();
        const showDays = trialEndDate?.isAtOrAfter(now, 'day');
        const daysLeft: number = Math.ceil(trialEndDate?.diff(now, 'd', true) ?? 0);
        const countColor = daysLeft < 5 ? 'red' : daysLeft < 10 ? 'orange' : 'green';
        return (
            <div className='trial-counter'>
                {!!showDays && (
                    <>
                        <label className='count-label'>{t('header.trialLeft', 'Demo nog')} </label>
                        <span className={classNames('count', countColor)}>{daysLeft}</span>
                        <label className='count-label'>
                            {' '}
                            {daysLeft === 1 ? t('header.day', 'dag') : t('header.days', 'dagen')}
                        </label>
                    </>
                )}
                <div className={classNames('upgrade-btn', upgradeClicked && 'inactive')} onClick={onClickUpgrade}>
                    <label className='btn-label'>{t('header.startSubscription', 'Abonnement starten')} </label>
                    <AwesomeIcon name='arrow-right' />
                </div>
            </div>
        );
    }

    function onChangeLanguage(nextLanguage: string) {
        const normalized = normalizeLanguage(nextLanguage);
        if (normalized !== 'nl' && normalized !== 'en') return;
        setLanguage(normalized);
        void i18n.changeLanguage(normalized);
        void setUserLanguage(user, normalized);
    }

    function renderRightCluster() {
        return (
            <div className='header-actions'>
                {renderEnv()}
                {renderTrialCounter()}
                <DVRDGroupedSelect
                    value={language}
                    onChange={onChangeLanguage}
                    items={languageItems}
                    label={t('settings.language.label', 'Taal')}
                    className='header-language-select account-field'
                    highlightSelected
                />
                {user !== null && (
                    <div className='account-field'>
                        <ContextAccountController />
                    </div>
                )}
                {user !== null && (
                    <div className='account-field user-info'>
                        <Icon icon={userCircle} className='user-icon' />
                        <label>{user.fullName}</label>
                        <div className='sign-out-btn' onClick={onClickLogout}>
                            <Icon icon={signOut} />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    useEffect(() => {
        window.setTimeout(() => {
            attachListener();
            determineHeaderHeight();
        });
        return function () {
            removeListener();
        };
    }, []);

    useEffect(() => {
        const mql = window.matchMedia('(max-width: 1279px)');
        const handler = (event: MediaQueryListEvent) => {
            setCollapsed(event.matches);
            if (!event.matches) setAccountDialogActive(false);
        };
        setCollapsed(mql.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    return (
        <>
            <div id='invisible-header' style={{height: `${headerHeight}px`}} />
            <div className='bolmate-header' ref={headerRef}>
                <SystemMessagesController />
                <div className='header-inner'>
                    <div className='header-content left'>
                        <AwesomeIcon
                            name='bars'
                            className='menu-toggle only-mob'
                            onClick={toggleMenu}
                            title={
                                menuCollapsed
                                    ? t('header.expandMenu', 'Menu uitklappen')
                                    : t('header.collapseMenu', 'Menu inklappen')
                            }
                        />
                        <div style={collapsed ? undefined : {width: `${menuWidth}px`}} className='logo-container'>
                            <Image name='logo' className='logo' onClick={onClickLogo} onLoad={determineHeaderHeight} />
                        </div>
                        <HeaderCrumbs />
                    </div>
                    <div className='header-content right'>
                        {collapsed ? (
                            <div className='account-trigger' onClick={() => setAccountDialogActive(true)}>
                                <Icon icon={userCircle} className='user-icon' />
                            </div>
                        ) : (
                            renderRightCluster()
                        )}
                    </div>
                </div>
            </div>
            {collapsed && (
                <BolmateDialog
                    active={accountDialogActive}
                    onClose={() => setAccountDialogActive(false)}
                    title={t('header.accountMenu', 'Account')}
                    className='header-account-dialog'
                    width='28rem'
                    hideCancel
                >
                    {renderRightCluster()}
                </BolmateDialog>
            )}
        </>
    );
}
